import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

import { useConfirm } from '../hooks/useConfirm';
import {
  useUnsavedChangesCopy,
  useUnsavedChangesRegistry,
} from '../hooks/useUnsavedChanges';

/**
 * Single, app-wide guard against losing unsaved changes.
 *
 * Render once **inside the data router** (e.g. in MainLayout) and **inside a
 * `<ConfirmProvider>` and `<UnsavedChangesProvider>`**. It owns:
 *
 *   - the only `useBlocker` in the app — blocks in-app navigation (sidebar,
 *     links, browser back/forward) whenever any registered form is dirty, and
 *     surfaces the shared styled confirm dialog;
 *   - the `beforeunload` listener — covers hard exits (tab close, refresh,
 *     typing another URL) with the browser's native dialog.
 *
 * Forms arm the guard automatically via `useDirtyTracker`; nothing else needs
 * to be wired per page.
 *
 * Requires a data router (`createBrowserRouter` / `RouterProvider`) — `useBlocker`
 * throws inside the declarative `<BrowserRouter>`.
 */
export default function UnsavedChangesGuard() {
  const registry = useUnsavedChangesRegistry();
  const copy = useUnsavedChangesCopy();
  const confirm = useConfirm();
  const anyDirty = registry?.anyDirty ?? false;

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!anyDirty) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;
    // One-shot bypass for the post-save redirect (see allowNextNavigation).
    if (registry?.consumeAllowNextNavigation()) return false;
    return true;
  });

  // When a navigation is blocked, ask the user with the shared styled dialog.
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let active = true;
    (async () => {
      const ok = await confirm({
        title: copy.title,
        message: copy.message,
        confirmLabel: copy.confirmLabel,
        variant: 'warning',
      });
      if (!active) return;
      if (ok) blocker.proceed?.();
      else blocker.reset?.();
    })();
    return () => {
      active = false;
    };
    // `blocker` identity changes with its state; `copy`/`confirm` are stable.
  }, [blocker, confirm, copy]);

  // Hard exits (refresh / tab close / external URL) — native browser dialog.
  useEffect(() => {
    if (!anyDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  return null;
}
